import React from 'react';
import isEqual from 'react-fast-compare';
import {useSelector} from 'react-redux';
import WeatherService from '../api/actions/weather';
import type {PointNowItem} from '../api/types';
import {selectIsPremium} from '../store/billing/selectors';
import {selectPlacesForecastModels} from '../store/location/selectors';
import type {CommonRootState} from '../store/types';
import {getDebugEmail, selectUserEmail} from '../store/user/selectors';
import {
  buildNowRequest,
  chunkNowItems,
  isNowFresh,
  isSamePlace,
} from '../utils/MyWeather/now';
import type {
  NowErrorReason,
  NowPlace,
  NowRequestItem,
  NowState,
} from '../utils/MyWeather/now';
import useApiClient from './useApiClient';

/**
 * The current hour of every place row of the My weather list (FE #381), from
 * `POST forecast/point/now/` (backend #267).
 *
 * Each place asks for the model its forecast screen resolves
 * (`selectPlacesForecastModels`), so a row agrees with the screen it opens
 * (#378 rule 3). Only what is missing or stale is asked, one call for all
 * of it (one per 20 past the server cap). Answers are kept in memory for
 * the session (`NOW_TTL_MS`, same UTC hour): a list reopened meanwhile
 * shows them at once and asks nothing; a stale one shows them while it asks
 * again. Nothing polls: a call goes out only when a list mounts, its places
 * change, or a place's resolved model does.
 *
 * `hold`: places whose model list is still being fetched
 * (`useMissingPlaceForecastModels`'s answer). A premium user's held row
 * waits, rather than show GFS and then swap to the model its list brings;
 * while any is held, at most one call goes out, and the held ones go
 * together once none is left: at most two calls for a list opened with no
 * model list known. A free user is not held: GFS whatever the list says.
 *
 * A failed refresh, of the whole call or of one item, keeps the last good
 * values: they say the hour they are for. With no good value yet, a row
 * says why it has none: the forecast, or the phone being offline. Another
 * account, or a change of plan, starts the cache over.
 */

type Entry = {
  fetchedAt: number;
  /** The item's last answer, an `error` one included. */
  latest: PointNowItem;
  /** Its last answer with values, kept across failed refreshes. */
  lastGood: PointNowItem | null;
};

/** Item keys whose call failed, and why. */
type Failures = Record<string, NowErrorReason>;

const cache = new Map<string, Entry>();
/** Each item key's call in flight, shared by lists mounting together. */
const inflight = new Map<string, Promise<Failures>>();
/**
 * What each place last showed, and where it was: a place that moves (the
 * Current location) keeps its readings while a fix at the same place asks
 * for its new ones (FE #381 decision 3).
 */
const lastByHash = new Map<
  string,
  {coordinates: NowPlace['coordinates']; item: PointNowItem}
>();
/** Whose answers the cache holds: account and plan (see `accountOf`). */
let cacheAccount: string | null = null;

/** Forget the session's answers. */
export const clearPlacesNowCache = () => {
  cache.clear();
  inflight.clear();
  lastByHash.clear();
  cacheAccount = null;
};

/**
 * Who the answers are for. The server serves GFS to a free or signed-out
 * user: a sign-in, a sign-out, a Login As or a plan change must not show
 * the previous one's rows.
 */
const accountOf = (state: CommonRootState) =>
  `${selectUserEmail(state)}|${getDebugEmail(state) ?? ''}|${selectIsPremium(state)}`;

const hasValues = (item: PointNowItem) =>
  item.error === null && item.timestamp !== null;

/** `useApiClient`'s code for a phone with no network (apisauce NETWORK_ERROR). */
const isOffline = (error: unknown) =>
  (error as {details?: {code?: unknown}} | null)?.details?.code ===
  'networkError';

type Fetch = ReturnType<typeof useApiClient>['fetch'];

/**
 * Asks for `items` for `account`. An answer that lands after the account or
 * plan changed is dropped: the cache it would fill was started over.
 */
const ask = async (
  fetch: Fetch,
  items: NowRequestItem[],
  account: string,
): Promise<Failures> => {
  const failures: Failures = {};
  await Promise.all(
    chunkNowItems(items).map(async (chunk) => {
      try {
        const response = await fetch(
          WeatherService.locationWeatherNow(chunk.map(({item}) => item)),
          // The row says what went wrong; a snackbar per refresh would not.
          {silent: true},
        );
        if (cacheAccount !== account) {
          return;
        }
        const fetchedAt = Date.now();
        chunk.forEach(({key}, index) => {
          const item = response?.items?.[index];
          if (!item) {
            failures[key] = 'forecast';
            return;
          }
          cache.set(key, {
            fetchedAt,
            latest: item,
            lastGood: hasValues(item)
              ? item
              : (cache.get(key)?.lastGood ?? null),
          });
        });
      } catch (error) {
        if (cacheAccount !== account) {
          return;
        }
        const reason = isOffline(error) ? 'offline' : 'forecast';
        chunk.forEach(({key}) => {
          failures[key] = reason;
        });
      }
    }),
  );
  return failures;
};

const stateOf = (
  place: NowPlace,
  key: string,
  failures: Failures,
  held: boolean,
): NowState => {
  const entry = held ? undefined : cache.get(key);
  if (entry?.lastGood) {
    return {status: 'data', item: entry.lastGood};
  }
  const previous = lastByHash.get(place.hash);
  if (previous && isSamePlace(previous.coordinates, place.coordinates)) {
    return {status: 'data', item: previous.item};
  }
  if (held) {
    return {status: 'loading'};
  }
  if (failures[key]) {
    return {status: 'error', reason: failures[key]};
  }
  if (entry) {
    // Answered, with an error and no good value ever: the row says so.
    return {status: 'data', item: entry.latest};
  }
  return {status: 'loading'};
};

const NO_HOLD: string[] = [];
const NO_FAILURES: Failures = {};

/**
 * `next`, with each place that did not change keeping its object from
 * `previous`, and `previous` itself when none did.
 */
const keepUnchanged = (
  previous: Record<string, NowState>,
  next: Record<string, NowState>,
): Record<string, NowState> => {
  const hashes = Object.keys(next);
  let same = hashes.length === Object.keys(previous).length;
  const kept: Record<string, NowState> = {};
  for (const hash of hashes) {
    const old = previous[hash];
    if (old !== undefined && isEqual(old, next[hash])) {
      kept[hash] = old;
    } else {
      kept[hash] = next[hash];
      same = false;
    }
  }
  return same ? previous : kept;
};

/**
 * Each place's readings, by place hash. Pass a stable `places` array (a
 * memo): a new one with the same items re-checks the cache but asks nothing
 * more.
 */
const usePlacesNow = (
  places: NowPlace[],
  {hold = NO_HOLD}: {hold?: string[]} = {},
): Record<string, NowState> => {
  const {fetch} = useApiClient();
  const account = useSelector(accountOf);
  const premium = useSelector(selectIsPremium);
  // Each place's model as its forecast screen resolves it: a model list
  // landing, a new override, order or plan changes the item, and so asks.
  const models = useSelector<
    CommonRootState,
    ReturnType<typeof selectPlacesForecastModels>
  >(
    (state: CommonRootState) =>
      selectPlacesForecastModels(
        state,
        places.map((place) => place.hash),
      ),
    isEqual,
  );
  const request = React.useMemo(
    () => buildNowRequest(places, models),
    [places, models],
  );
  // Keyed on the places, not the array: a new `hold` with the same ones
  // (each list render) is the same set, and a free user is never held.
  const heldKey = premium ? JSON.stringify(hold) : '[]';
  const held = React.useMemo(
    () => new Set<string>(JSON.parse(heldKey)),
    [heldKey],
  );
  // A new object at every answer: it is what re-reads the cache.
  const [outcome, setOutcome] = React.useState<{failures: Failures}>({
    failures: NO_FAILURES,
  });
  // Whether a call went out while places were held (at most one does).
  const askedWhileHolding = React.useRef(false);

  React.useEffect(() => {
    if (cacheAccount !== account) {
      cache.clear();
      inflight.clear();
      lastByHash.clear();
      cacheAccount = account;
      // A new account starts its own count of calls made while holding.
      askedWhileHolding.current = false;
    }
    // An item asked for by a held place only waits too.
    const shown = new Set(
      places
        .filter((place) => !held.has(place.hash))
        .map((place) => request.keyByHash[place.hash]),
    );
    const items = request.items.filter(({key}) => shown.has(key));
    const now = Date.now();
    const todo = items.filter(({key}) => {
      const entry = cache.get(key);
      const fresh = entry !== undefined && isNowFresh(entry.fetchedAt, now);
      return !fresh && !inflight.has(key);
    });
    const flights = items
      .map(({key}) => inflight.get(key))
      .filter((flight): flight is Promise<Failures> => flight !== undefined);
    const holding = held.size > 0;
    if (todo.length > 0 && !(holding && askedWhileHolding.current)) {
      askedWhileHolding.current = holding;
      const flight = ask(fetch, todo, account);
      todo.forEach(({key}) => inflight.set(key, flight));
      flight.finally(() => {
        todo.forEach(({key}) => {
          if (inflight.get(key) === flight) {
            inflight.delete(key);
          }
        });
      });
      flights.push(flight);
    }
    if (flights.length === 0) {
      return;
    }
    let live = true;
    Promise.all([...new Set(flights)]).then((results) => {
      const failures = Object.assign({}, ...results) as Failures;
      for (const place of places) {
        const good = cache.get(request.keyByHash[place.hash])?.lastGood;
        if (good && !held.has(place.hash)) {
          lastByHash.set(place.hash, {
            coordinates: place.coordinates,
            item: good,
          });
        }
      }
      if (live) {
        setOutcome((previous) => {
          const next = {...previous.failures};
          items.forEach(({key}) => {
            delete next[key];
          });
          return {failures: {...next, ...failures}};
        });
      }
    });
    return () => {
      live = false;
    };
  }, [account, fetch, held, places, request]);

  const states = React.useMemo(() => {
    const next: Record<string, NowState> = {};
    const current = cacheAccount === account;
    for (const place of places) {
      next[place.hash] = current
        ? stateOf(
            place,
            request.keyByHash[place.hash],
            outcome.failures,
            held.has(place.hash),
          )
        : {status: 'loading'};
    }
    return next;
  }, [account, held, outcome, places, request]);

  // What the rows were handed last. Each recompute (an answer, a new
  // `places`, a model list landing) builds every place's state anew, and the
  // list hands each to a memoised row, which then re-ran its formatting for
  // nothing (FE #418): an unchanged place keeps its object, and the record
  // stays the same one when no place changed. Updated during render, React's
  // pattern for information from previous renders.
  const [shown, setShown] = React.useState(states);
  const stable = React.useMemo(
    () => keepUnchanged(shown, states),
    [shown, states],
  );
  if (stable !== shown) {
    setShown(stable);
  }
  return stable;
};

export default usePlacesNow;
